import { type Dtos, type SfaaCodeId, type SuretyType } from "@inrev/common";
import { useAdminUpdateBond } from "@portal/api/bond/admin";
import { useFetchRecentlyUsedSfaaCodes, useFetchSfaaCodes } from "@portal/api/sfaa-code/admin";
import { isSfaaContractObligee } from "@portal/components/features/sfaa-code/sfaaCodeFilters";
import { SfaaCodeSelectInput } from "@portal/components/form/inputs/SfaaCodeSelectInput";
import { Card, Text } from "@portal/components/primitives";
import { useEffect, useState } from "react";
import { AdminDataList, type AdminDataListProps } from "../../shared/admin/AdminDataList";
import { NAICSCodesList } from "./NAICSCodeList";

type AdminBondResponse = Dtos.Admin.SuretyBond.Get.Response;

type AdminBondDetailsBond = Pick<AdminBondResponse, "id" | "accountId" | "sfaaCode"> & {
	// The response schema only includes contract, so suretyType stays SuretyType
	// instead of a Pick.
	suretyType: SuretyType;
	project: {
		input: Pick<AdminBondResponse["project"]["input"], "scopeOfWork">;
	};
};

export type AdminBondDetailsCardProps = {
	bond: AdminBondDetailsBond;
};

const SfaaCodeRow = ({
	bondId,
	accountId,
	sfaaCode,
	suretyType,
}: Pick<AdminBondDetailsBond, "sfaaCode" | "suretyType"> & {
	bondId: AdminBondDetailsBond["id"];
	accountId: AdminBondDetailsBond["accountId"];
}) => {
	const savedId = sfaaCode?.id ?? null;
	const [selectedId, setSelectedId] = useState<SfaaCodeId | null>(savedId);
	const { updateBond, updateBondIsLoading } = useAdminUpdateBond(bondId, accountId);
	const { sfaaCodes, sfaaCodesError, sfaaCodesIsLoading } = useFetchSfaaCodes();
	const { recentlyUsedSfaaCodes } = useFetchRecentlyUsedSfaaCodes(accountId, { suretyType });
	const initialOwnerType =
		sfaaCode && isSfaaContractObligee(sfaaCode.obligeeType) ? sfaaCode.obligeeType : undefined;

	useEffect(() => {
		setSelectedId(savedId);
	}, [savedId]);

	const selectSfaaCode = (id: SfaaCodeId | undefined) => {
		const nextId = id ?? null;
		if (nextId === savedId || updateBondIsLoading) return;
		setSelectedId(nextId);
		updateBond({ sfaaCodeId: nextId }, { onError: () => setSelectedId(savedId) });
	};

	return (
		<div
			className="flex w-full max-w-[420px] flex-col gap-1"
			data-testid="admin-bond-details-sfaa-code"
		>
			<SfaaCodeSelectInput
				sfaaCodes={sfaaCodes}
				recentlyUsed={recentlyUsedSfaaCodes}
				isLoading={sfaaCodesIsLoading}
				suretyType={suretyType}
				initialOwnerType={initialOwnerType}
				value={selectedId ?? undefined}
				onChange={selectSfaaCode}
				disabled={updateBondIsLoading}
				compact
			/>
			{sfaaCodesError ? (
				<Text type="caption" color="danger">
					Couldn't load SFAA codes. Try again.
				</Text>
			) : null}
		</div>
	);
};

export const AdminBondDetailsCard = ({ bond }: AdminBondDetailsCardProps) => {
	const rows: AdminDataListProps["data"] = [
		{
			label: "Scope of Work",
			value: (
				<NAICSCodesList
					naicsCodes={bond.project.input.scopeOfWork}
					showRatio
					emptyMessage="Unavailable"
					className="min-w-[400px] max-w-[400px]"
				/>
			),
		},
		{
			label: "SFAA Code",
			value: (
				<SfaaCodeRow
					bondId={bond.id}
					accountId={bond.accountId}
					sfaaCode={bond.sfaaCode}
					suretyType={bond.suretyType}
				/>
			),
		},
	];

	return (
		<Card elevation="raised" className="rounded-xl" data-testid="admin-bond-details-card">
			<Card.Body className="p-6">
				<div className="flex w-full flex-col space-y-5">
					<Text type="title" color="primary">
						Bond Details
					</Text>
					<div className="flex flex-col divide-y divide-bc-default">
						<AdminDataList bare data={rows} />
					</div>
				</div>
			</Card.Body>
		</Card>
	);
};
